'use client';

import { Button } from '@instamenu/ui-system-next';
import { breakpoints } from '@instamenu/ui-vanilla-extract';
import { useRouter } from 'next/navigation';
import { useMediaQuery } from 'react-responsive';
import { observer } from 'mobx-react-lite';
import { Store, Utensils } from 'lucide-react';
import { OrderStatus } from '@instamenu/data-access';
import { useOrderPolling, type Order } from '@instamenu/context-checkout';

import { CompleteLoyalty } from '../loyalty-status/complete-loyalty';
import { EnrollmentCard3D } from '../loyalty-status/enrollment-card';
import {
  ActionRow,
  InfoCard,
  OrderCompleteHero,
  OrderCompleteLayout,
  OrderCompleteStack,
  OrderCompleteTwoColumn,
  OrderSummary,
  StatusPill,
} from './components';
import { getTablePillContent } from './status-pill-content';

type TableOrderProps = {
  order: Order;
  handleBack: () => void;
};

export const TableOrder = observer(({ order, handleBack }: TableOrderProps) => {
  const router = useRouter();
  const isDesktop = useMediaQuery({ query: breakpoints.tablet });
  const greeting = `Thanks${order.user?.firstName ? `, ${order.user.firstName}` : ''}!`;

  // Poll for status updates so the pill ticks through "Order received" →
  // "Being prepared" → "Served" as the kitchen advances the ticket. Pause
  // once the order is in a terminal state.
  useOrderPolling(order, {
    pause: order.status === OrderStatus.Completed || order.status === OrderStatus.Canceled,
  });

  const pillContent = getTablePillContent(order);
  const statusPill = (
    <StatusPill
      tone={pillContent.tone}
      icon={pillContent.icon}
      data-id="order-complete-status-pill"
    >
      {pillContent.label}
    </StatusPill>
  );

  const locationName =
    order.tableMapping ||
    order.table ||
    (order.isPickupLocation ? 'your pickup location' : 'your table');
  const scheduledTime = order.scheduledAt?.toFormat('LLLL d, h:mm a');
  const pickupDescription = scheduledTime
    ? `Pick up your order at ${locationName} on ${scheduledTime}.`
    : `Pick up your order at ${locationName} when it's ready.`;
  const tableDescription = scheduledTime
    ? `We'll bring your order to ${locationName} at your scheduled time.`
    : `We'll bring your order to ${locationName} as soon as it's ready`;

  const tableCard = (
    <InfoCard
      icon={
        order.isPickupLocation ? (
          <Store size={18} strokeWidth={2} />
        ) : (
          <Utensils size={18} strokeWidth={2} />
        )
      }
      title={order.isPickupLocation ? `Pickup at ${locationName}` : `Dine-in at ${locationName}`}
      subtitle={order.isPickupLocation ? pickupDescription : tableDescription}
      data-id="order-complete-table-card"
    />
  );

  const summary = <OrderSummary order={order} />;

  // Match the Pickup / Delivery layout: primary + secondary buttons side by
  // side so the action chrome is consistent across all three states.
  const actions = (
    <ActionRow>
      <Button css={{ flex: 1 }} onClick={handleBack} data-id="order-complete-order-more">
        Order More
      </Button>
      <Button
        variant="secondary"
        css={{ flex: 1 }}
        onClick={() => router.push(`${process.env.NEXT_PUBLIC_API_ENDPOINT}/orders/${order.id}`)}
        data-id="order-complete-view-receipt"
      >
        View Receipt
      </Button>
    </ActionRow>
  );

  const loyalty = (
    <>
      <CompleteLoyalty />
      <EnrollmentCard3D />
    </>
  );

  const hero = <OrderCompleteHero pill={statusPill} greeting={greeting} />;

  return (
    <OrderCompleteLayout hero={hero}>
      {isDesktop ? (
        <OrderCompleteTwoColumn
          left={
            <OrderCompleteStack>
              {tableCard}
              {actions}
              {loyalty}
            </OrderCompleteStack>
          }
          right={<OrderCompleteStack>{summary}</OrderCompleteStack>}
        />
      ) : (
        <OrderCompleteStack>
          {tableCard}
          {summary}
          {actions}
          {loyalty}
        </OrderCompleteStack>
      )}
    </OrderCompleteLayout>
  );
});
